import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";

import { Response } from "../types";

import { Model } from "./types";

import { jsonAuthHeaders } from "@/utils/headers";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { useAuth } from "@/context/auth-context";
import { ENDPOINTS } from "@/api/application-api";

export const useModels = ({
  providerId,
  q,
  allowEmptySearch = false,
  fromProviderId,
  apiType,
  enabled,
}: {
  providerId?: number;
  q?: string;
  allowEmptySearch?: boolean;
  fromProviderId?: number;
  // Scope large catalogs (e.g. OpenRouter) to one api_type bucket.
  apiType?: string | null;
  enabled?: boolean;
}) => {
  const authContext = useAuth();
  const { workspaceId } = useParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);

  // Fetch lazily: only hit the backend once we know which slice to load
  // (a provider, or an explicit search). Without this the hook eagerly pulled
  // the whole workspace catalog before any provider was even selected.
  const isEnabled = enabled ?? (Boolean(providerId) || allowEmptySearch || Boolean(q));

  const { data, isLoading, isRefetching, isFetching } = useQuery<
    Response<Model[], "model_configs", string>
  >({
    enabled: isEnabled,
    // Cache each (provider, api_type) bucket separately.
    staleTime: 5 * 60 * 1000,
    queryKey: ["models", workspaceId, providerId, page, pageSize, q, fromProviderId, apiType],
    queryFn: async () => {
      const url = new URL(ENDPOINTS.models(workspaceId || ""));
      url.searchParams.set("page", page.toString());
      url.searchParams.set("per_page", pageSize.toString());
      if (providerId) {
        url.searchParams.set("provider_id", providerId?.toString() || "");
      }

      if (apiType) {
        url.searchParams.set("api_type", apiType);
      }

      if (allowEmptySearch || q) {
        url.searchParams.set("q", q || "");
      }

      const response = await fetch(`${url}`, {
        headers: jsonAuthHeaders(authContext?.userToken ?? ""),
        method: "GET",
      });
      return handleResponse(response);
    },
  });

  return {
    data,
    // React Query v4 reports isLoading=true for a *disabled* query (no provider
    // selected yet). Gate it so callers don't render a permanent loading
    // skeleton before there's anything to fetch.
    isLoading: isEnabled && isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    isRefetching,
    isFetching,
  };
};
